import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Package, Calendar, DollarSign, Truck, ArrowLeft, 
  Car, User, FileText, Image, Download, Eye, Gauge, Cog, Fuel, Clock
} from 'lucide-react';
import axios from 'axios';
import PublicHeader from '../public/PublicHeader';
import PublicFooter from '../public/PublicFooter';
import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const MyOrders = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [vehicleDocuments, setVehicleDocuments] = useState({});
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/customer/my-orders`, {
        headers: getAuthHeaders()
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'available') return 'bg-cyan-100 text-cyan-700';
    if (statusLower === 'reserved') return 'bg-purple-100 text-purple-700';
    if (statusLower === 'reserve_cancellation') return 'bg-orange-100 text-orange-700';
    if (statusLower === 'pending') return 'bg-yellow-100 text-yellow-700';
    if (statusLower === 'purchased') return 'bg-blue-100 text-blue-700';
    if (statusLower === 'shipped') return 'bg-indigo-100 text-indigo-700';
    if (statusLower === 'on_the_way') return 'bg-sky-100 text-sky-700';
    if (statusLower === 'delivered') return 'bg-green-100 text-green-700';
    if (statusLower === 'cancelled') return 'bg-red-100 text-red-700';
    if (statusLower === 'sold') return 'bg-emerald-100 text-emerald-700';
    return 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return null;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return `$${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getPrimaryVehicle = (order) => {
    return order?.vehicles?.[0] || order?.vehicle || null;
  };

  const getOrderTotalUsd = (order) => {
    return order?.total_price_usd ?? order?.total_amount_usd ?? 0;
  };

  const getPaidUsd = (order) => {
    if (typeof order?.total_paid_usd === 'number') return order.total_paid_usd;
    if (typeof order?.payment_summary?.total_paid_usd === 'number') return order.payment_summary.total_paid_usd;
    if (Array.isArray(order?.payments)) {
      return order.payments.reduce((sum, p) => sum + (p.amount_usd || 0), 0);
    }
    return 0;
  };

  const getBalanceUsd = (order) => {
    const total = getOrderTotalUsd(order);
    const paid = getPaidUsd(order);
    return Math.max(0, total - paid);
  };

  const handleViewDetails = async (order) => {
    let freshOrderData = order;
    try {
      // Fetch fresh order data from API to get updated vehicle info
      const response = await axios.get(`${BACKEND_URL}/api/public/customer/order/${order.id}`, {
        headers: getAuthHeaders()
      });
      freshOrderData = response.data;
    } catch (error) {
      // Keep cached order data if API fails
      console.warn('Could not fetch fresh order data, using cached:', error);
    }
    setSelectedOrder(freshOrderData);
    setShowDetailDialog(true);
    
    // Get vehicles from either fresh or cached data
    const vehiclesToUse = freshOrderData.vehicles || order.vehicles || [];
    
    // Fetch documents for each vehicle in the order using public customer endpoint
    if (vehiclesToUse.length > 0) {
      setLoadingDocuments(true);
      const docsMap = {};
      
      for (const vehicle of vehiclesToUse) {
        if (vehicle.id) {
          try {
            const response = await axios.get(`${BACKEND_URL}/api/public/customer/vehicle/${vehicle.id}/documents`, {
              headers: getAuthHeaders()
            });
            docsMap[vehicle.id] = response.data;
          } catch (error) {
            console.error('Error fetching documents for vehicle:', vehicle.id, error);
            docsMap[vehicle.id] = [];
          }
        }
      }
      
      setVehicleDocuments(docsMap);
      setLoadingDocuments(false);
    }
  };

  const getVehicleDocuments = (vehicle) => {
    const docs = [];
    
    // First add vehicle-level document fields
    if (vehicle.export_certificate_url) docs.push({ name: 'Export Certificate', url: vehicle.export_certificate_url });
    if (vehicle.inspection_report_url) docs.push({ name: 'Inspection Report', url: vehicle.inspection_report_url });
    if (vehicle.shipping_docs_url) docs.push({ name: 'Shipping Documents', url: vehicle.shipping_docs_url });
    if (vehicle.bl_copy_url) docs.push({ name: 'Bill of Lading (Copy)', url: vehicle.bl_copy_url });
    if (vehicle.invoice_url) docs.push({ name: 'Invoice', url: vehicle.invoice_url });
    
    // Add documents from Document Management (if loaded)
    const docManagerDocs = vehicleDocuments[vehicle.id] || [];
    docManagerDocs.forEach(doc => {
      // Avoid duplicates - check if URL already exists
      const exists = docs.some(d => d.url === doc.file_url);
      if (!exists && doc.file_url) {
        // Use original_filename if available, otherwise format document_type
        const displayName = doc.original_filename || 
          (doc.document_type ? doc.document_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Document');
        docs.push({ name: displayName, url: doc.file_url });
      }
    });
    
    return docs;
  };

  const downloadDocument = async (doc) => {
    try {
      const response = await fetch(doc.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name.replace(/\s+/g, '_') + '.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      window.open(doc.url, '_blank');
    }
  };

  const downloadAllImages = async (vehicle) => {
    if (vehicle.images && vehicle.images.length > 0) {
      for (let i = 0; i < vehicle.images.length; i++) {
        const imgUrl = vehicle.images[i];
        const filename = `${vehicle.stock_no || 'vehicle'}_image_${i + 1}.jpg`;
        
        try {
          const response = await fetch(imgUrl);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          // Small delay between downloads
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (error) {
          console.error('Error downloading image:', error);
          // Fallback: open in new tab
          const link = document.createElement('a');
          link.href = imgUrl;
          link.target = '_blank';
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicHeader currency="USD" onCurrencyChange={() => {}} />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading orders...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader currency="USD" onCurrencyChange={() => {}} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/customer/dashboard')}
            variant="ghost"
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">My Orders</h1>
          <p className="text-gray-600 mt-1">Track all your vehicle orders and shipments</p>
        </div>

        {/* Orders List */}
        {orders.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No orders yet</h3>
              <p className="text-gray-600 mb-6">Your orders will appear here once created</p>
              <Button
                onClick={() => navigate('/vehicles')}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
              >
                Browse Vehicles
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const primaryVehicle = getPrimaryVehicle(order);
              const totalUsd = getOrderTotalUsd(order);
              const paidUsd = getPaidUsd(order);
              const balanceUsd = getBalanceUsd(order);
              const displayTitle = primaryVehicle
                ? `${primaryVehicle.year || ''} ${primaryVehicle.brand || ''} ${primaryVehicle.model || ''}`.trim() || 'Vehicle'
                : 'Vehicle details pending';
              const displayStock = primaryVehicle?.stock_no;
              const displayImage = primaryVehicle?.images?.[0];
              const displayPod = order.pod || order.destination_port || primaryVehicle?.pod || 'TBD';

              return (
              <Card key={order.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Vehicle Image */}
                    <div className="lg:col-span-1">
                      {displayImage ? (
                        <img
                          src={displayImage}
                          alt={primaryVehicle?.model || 'Vehicle'}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                          <Package className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Order Details */}
                    <div className="lg:col-span-3">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {order.vehicles?.length > 1 ? (
                                `${order.vehicles.length} Vehicles`
                              ) : (
                                displayTitle
                              )}
                            </h3>
                            <Badge className={getStatusColor(order.status)}>
                              {order.status || 'Pending'}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600">Order ID: #{order.id.slice(-8).toUpperCase()}</p>
                          {displayStock && (
                            <p className="text-sm text-gray-600">Stock No: {displayStock}</p>
                          )}
                          {order.vehicles?.length > 1 && (
                            <p className="text-xs text-gray-500 mt-1">
                              {order.vehicles.map(v => v.stock_no).join(', ')}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-red-600">
                            ${totalUsd ? totalUsd.toLocaleString() : 'N/A'}
                          </p>
                          {paidUsd > 0 && (
                            <p className="text-sm text-green-600 font-medium">
                              Paid: ${paidUsd.toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Payment Progress Bar */}
                      {totalUsd > 0 && (
                        <div className="mb-4">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Payment Progress</span>
                            <span className="font-medium">
                              {formatCurrency(paidUsd)} paid of {formatCurrency(totalUsd)}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-500 h-2 rounded-full transition-all"
                              style={{ width: `${Math.min(100, (paidUsd / totalUsd) * 100)}%` }}
                            />
                          </div>
                          {balanceUsd > 0 && (
                            <p className="text-sm text-orange-600 mt-1">
                              Balance Due: {formatCurrency(balanceUsd)}
                            </p>
                          )}
                        </div>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="flex items-center text-gray-600 mb-1">
                            <Calendar className="h-4 w-4 mr-1" />
                            <span>Order Date</span>
                          </div>
                           <p className="font-semibold">{formatDate(order.order_date || order.created_at) || 'N/A'}</p>
                        </div>

                        <div>
                          <div className="flex items-center text-gray-600 mb-1">
                            <DollarSign className="h-4 w-4 mr-1" />
                            <span>Payment Status</span>
                          </div>
                          <Badge className={order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {order.payment_status || 'Pending'}
                          </Badge>
                        </div>

                        <div>
                          <div className="flex items-center text-gray-600 mb-1">
                            <Truck className="h-4 w-4 mr-1" />
                            <span>Destination</span>
                          </div>
                          <p className="font-semibold">{displayPod}</p>
                        </div>

                        <div>
                          <div className="flex items-center text-gray-600 mb-1">
                            <Clock className="h-4 w-4 mr-1" />
                            <span>ETD</span>
                          </div>
                          <p className="font-semibold">{formatDate(primaryVehicle?.etd) || 'TBD'}</p>
                        </div>

                        <div>
                          <div className="flex items-center text-gray-600 mb-1">
                            <Clock className="h-4 w-4 mr-1" />
                            <span>ETA</span>
                          </div>
                          <p className="font-semibold">{formatDate(primaryVehicle?.eta) || 'TBD'}</p>
                        </div>

                        <div>
                          <div className="flex items-center text-gray-600 mb-1">
                            <Calendar className="h-4 w-4 mr-1" />
                            <span>Est. Delivery</span>
                          </div>
                          <p className="font-semibold">{formatDate(order.estimated_delivery_date || primaryVehicle?.eta) || 'TBD'}</p>
                        </div>
                      </div>

                      {/* View Details Button */}
                      <div className="mt-4 flex justify-end">
                        <Button 
                          onClick={() => handleViewDetails(order)}
                          variant="outline"
                          className="flex items-center gap-2"
                        >
                          <Eye className="h-4 w-4" />
                          View Details
                        </Button>
                      </div>

                      {primaryVehicle?.tracking_number && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm">
                          <p className="font-semibold text-blue-900 mb-1">Tracking Information</p>
                          <p className="text-blue-900">
                            {(primaryVehicle?.tracking_provider || 'Carrier')}:
                            {' '}
                            {primaryVehicle?.tracking_number}
                          </p>
                        </div>
                      )}

                      {/* Payment Details */}
                      {order.payments && order.payments.length > 0 && (
                        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm font-semibold text-green-900 mb-2">Payment History</p>
                          <div className="space-y-1">
                            {order.payments.map((payment, idx) => (
                              <div key={`payment-${idx}`} className="flex justify-between text-sm">
                                <span className="text-gray-700">
                                   {formatDate(payment.payment_date) || 'N/A'} - {payment.payment_method || 'Payment'}
                                </span>
                                <span className="font-semibold text-green-700">
                                  ${payment.amount_usd?.toLocaleString() || '0'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {order.notes && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600"><strong>Notes:</strong> {order.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Order Details Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <Tabs defaultValue="vehicle" className="w-full">
              <TabsList className="w-full">
                <TabsTrigger value="vehicle" className="flex-1">Vehicle Details</TabsTrigger>
                <TabsTrigger value="payment" className="flex-1">Payment Info</TabsTrigger>
                <TabsTrigger value="documents" className="flex-1">Documents</TabsTrigger>
                <TabsTrigger value="shipping" className="flex-1">Shipping</TabsTrigger>
              </TabsList>

              {/* Vehicle Details Tab */}
              <TabsContent value="vehicle" className="space-y-4 mt-4">
                {selectedOrder.vehicles?.map((vehicle, index) => (
                  <Card key={vehicle.id || index} className="border-2 border-slate-200">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">
                          {vehicle.year} {vehicle.make} {vehicle.model}
                        </CardTitle>
                        {selectedOrder.vehicles?.length > 1 && (
                          <Badge variant="outline">Vehicle {index + 1}</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Vehicle Images */}
                      {vehicle.images && vehicle.images.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-slate-900 flex items-center gap-2">
                              <Image className="h-4 w-4" />
                              Vehicle Images
                            </h4>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadAllImages(vehicle)}
                              className="flex items-center gap-1"
                            >
                              <Download className="h-4 w-4" />
                              Download All ({vehicle.images.length})
                            </Button>
                          </div>
                          <div className="grid grid-cols-4 gap-2">
                            {vehicle.images.slice(0, 8).map((img, idx) => (
                              <div key={idx} className="relative group">
                                <img 
                                  src={img} 
                                  alt={`Vehicle ${idx + 1}`}
                                  className="w-full h-20 object-cover rounded-lg"
                                />
                                <a 
                                  href={img} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                                >
                                  <Download className="h-6 w-6 text-white" />
                                </a>
                              </div>
                            ))}
                          </div>
                          {vehicle.images.length > 8 && (
                            <p className="text-sm text-slate-500 mt-2">+{vehicle.images.length - 8} more images</p>
                          )}
                        </div>
                      )}

                      {/* Vehicle Specifications */}
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2 text-slate-600 mb-1">
                            <Package className="h-4 w-4" />
                            <span className="text-sm">Stock No</span>
                          </div>
                          <p className="font-semibold">{vehicle.stock_no || 'N/A'}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2 text-slate-600 mb-1">
                            <Gauge className="h-4 w-4" />
                            <span className="text-sm">Mileage</span>
                          </div>
                          <p className="font-semibold">{vehicle.mileage ? `${vehicle.mileage.toLocaleString()} km` : 'N/A'}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2 text-slate-600 mb-1">
                            <Cog className="h-4 w-4" />
                            <span className="text-sm">Transmission</span>
                          </div>
                          <p className="font-semibold">{vehicle.transmission || 'N/A'}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2 text-slate-600 mb-1">
                            <Fuel className="h-4 w-4" />
                            <span className="text-sm">Fuel Type</span>
                          </div>
                          <p className="font-semibold">{vehicle.fuel_type || 'N/A'}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="text-slate-600 mb-1">
                            <span className="text-sm">Color</span>
                          </div>
                          <p className="font-semibold">{vehicle.color || 'N/A'}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="text-slate-600 mb-1">
                            <span className="text-sm">Chassis No</span>
                          </div>
                          <p className="font-semibold">{vehicle.chassis_no || 'N/A'}</p>
                        </div>
                        {vehicle.engine_capacity && (
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <div className="text-slate-600 mb-1">
                              <span className="text-sm">Engine Capacity</span>
                            </div>
                            <p className="font-semibold">{vehicle.engine_capacity}cc</p>
                          </div>
                        )}
                        {vehicle.drive_system && (
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <div className="text-slate-600 mb-1">
                              <span className="text-sm">Drive System</span>
                            </div>
                            <p className="font-semibold">{vehicle.drive_system}</p>
                          </div>
                        )}
                        {vehicle.steering && (
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <div className="text-slate-600 mb-1">
                              <span className="text-sm">Steering</span>
                            </div>
                            <p className="font-semibold">{vehicle.steering}</p>
                          </div>
                        )}
                        {vehicle.description && (
                          <div className="p-3 bg-slate-50 rounded-lg col-span-full">
                            <div className="text-slate-600 mb-1">
                              <span className="text-sm">Description</span>
                            </div>
                            <p className="font-semibold text-sm">{vehicle.description}</p>
                          </div>
                        )}
                        {vehicle.features && (
                          <div className="p-3 bg-slate-50 rounded-lg col-span-full">
                            <div className="text-slate-600 mb-1">
                              <span className="text-sm">Features</span>
                            </div>
                            <p className="font-semibold text-sm">{vehicle.features}</p>
                          </div>
                        )}
                      </div>

                      {/* Vehicle Pricing */}
                      {(vehicle.fob_usd || vehicle.fob_jpy || vehicle.freight_usd || vehicle.freight_jpy) && (
                        <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                          <h4 className="font-medium text-slate-900 mb-2">Vehicle Cost Breakdown</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            {vehicle.fob_usd && (
                              <div>
                                <span className="text-slate-600">FOB (USD):</span>
                                <p className="font-semibold">${vehicle.fob_usd.toLocaleString()}</p>
                              </div>
                            )}
                            {vehicle.fob_jpy && (
                              <div>
                                <span className="text-slate-600">FOB (JPY):</span>
                                <p className="font-semibold">¥{vehicle.fob_jpy.toLocaleString()}</p>
                              </div>
                            )}
                            {vehicle.freight_usd && (
                              <div>
                                <span className="text-slate-600">Freight (USD):</span>
                                <p className="font-semibold">${vehicle.freight_usd.toLocaleString()}</p>
                              </div>
                            )}
                            {vehicle.freight_jpy && (
                              <div>
                                <span className="text-slate-600">Freight (JPY):</span>
                                <p className="font-semibold">¥{vehicle.freight_jpy.toLocaleString()}</p>
                              </div>
                            )}
                            {vehicle.insurance_usd && (
                              <div>
                                <span className="text-slate-600">Insurance (USD):</span>
                                <p className="font-semibold">${vehicle.insurance_usd.toLocaleString()}</p>
                              </div>
                            )}
                            {vehicle.insurance_jpy && (
                              <div>
                                <span className="text-slate-600">Insurance (JPY):</span>
                                <p className="font-semibold">¥{vehicle.insurance_jpy.toLocaleString()}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>

              {/* Payment Info Tab */}
              <TabsContent value="payment" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Payment Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">Total Amount</p>
                        <p className="text-xl font-bold text-slate-900">
                          {formatCurrency(getOrderTotalUsd(selectedOrder))}
                        </p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-600">Amount Paid</p>
                        <p className="text-xl font-bold text-green-700">
                          {formatCurrency(getPaidUsd(selectedOrder))}
                        </p>
                      </div>
                      <div className="p-4 bg-orange-50 rounded-lg">
                        <p className="text-sm text-orange-600">Balance Due</p>
                        <p className="text-xl font-bold text-orange-700">
                          {formatCurrency(getBalanceUsd(selectedOrder))}
                        </p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">Payment Status</p>
                        <Badge className={selectedOrder.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                          {selectedOrder.payment_status || 'Pending'}
                        </Badge>
                      </div>
                    </div>

                    {/* Payment Progress */}
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-600">Payment Progress</span>
                        <span className="font-medium">
                          {Math.round((getPaidUsd(selectedOrder) / getOrderTotalUsd(selectedOrder)) * 100)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className="bg-green-500 h-3 rounded-full transition-all"
                          style={{ width: `${Math.min(100, (getPaidUsd(selectedOrder) / getOrderTotalUsd(selectedOrder)) * 100)}%` }}
                        />
                      </div>
                    </div>

                    {/* Payment History */}
                    {selectedOrder.payments && selectedOrder.payments.length > 0 && (
                      <div>
                        <h4 className="font-medium text-slate-900 mb-3">Payment History</h4>
                        <div className="space-y-2">
                          {selectedOrder.payments.map((payment, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div>
                                <p className="font-medium">{payment.payment_method || 'Payment'}</p>
                                <p className="text-sm text-slate-500">{formatDate(payment.payment_date) || 'N/A'}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-green-700">{formatCurrency(payment.amount_usd)}</p>
                                <Badge className={payment.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                                  {payment.status || 'Completed'}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents" className="space-y-4 mt-4">
                {loadingDocuments ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-4"></div>
                      <p className="text-slate-500">Loading documents...</p>
                    </CardContent>
                  </Card>
                ) : (
                  selectedOrder.vehicles?.map((vehicle, index) => {
                    const docs = getVehicleDocuments(vehicle);
                    return (
                      <Card key={vehicle.id || index}>
                        <CardHeader>
                          <CardTitle className="text-lg">
                            {vehicle.year} {vehicle.make} {vehicle.model} - Documents
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {docs.length > 0 ? (
                            <div className="space-y-2">
                              {docs.map((doc, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <FileText className="h-5 w-5 text-slate-500" />
                                    <span className="font-medium">{doc.name}</span>
                                  </div>
                                  <button 
                                    onClick={() => downloadDocument(doc)}
                                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                                  >
                                    <Download className="h-4 w-4" />
                                    Download
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-500 text-center py-4">No documents available</p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </TabsContent>

              {/* Shipping Tab */}
              <TabsContent value="shipping" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Shipping Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">Port of Loading</p>
                        <p className="font-semibold">{selectedOrder.pol || selectedOrder.port_of_loading || selectedOrder.vehicles?.[0]?.pol || 'TBD'}</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">Port of Discharge</p>
                        <p className="font-semibold">{selectedOrder.pod || selectedOrder.destination_port || selectedOrder.vehicles?.[0]?.pod || 'TBD'}</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">Shipping Method</p>
                        <p className="font-semibold">{selectedOrder.shipping_method || selectedOrder.vehicles?.[0]?.vessel_type || 'RoRo'}</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">Order Status</p>
                        <Badge className={getStatusColor(selectedOrder.status)}>
                          {selectedOrder.status || 'Pending'}
                        </Badge>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">ETD (Est. Departure)</p>
                        <p className="font-semibold">{formatDate(selectedOrder.vehicles?.[0]?.etd) || 'TBD'}</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">ETA (Est. Arrival)</p>
                        <p className="font-semibold">{formatDate(selectedOrder.vehicles?.[0]?.eta) || 'TBD'}</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">Estimated Delivery</p>
                        <p className="font-semibold">{formatDate(selectedOrder.estimated_delivery_date || selectedOrder.vehicles?.[0]?.eta) || 'TBD'}</p>
                      </div>
                      {(selectedOrder.actual_delivery_date || selectedOrder.vehicles?.[0]?.release_date) && (
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-600">Actual Delivery</p>
                           <p className="font-semibold">{formatDate(selectedOrder.actual_delivery_date || selectedOrder.vehicles?.[0]?.release_date) || 'TBD'}</p>
                        </div>
                      )}
                    </div>

                    {/* Tracking Info */}
                    {selectedOrder.vehicles?.some(v => v.tracking_number) && (
                      <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                        <h4 className="font-medium text-blue-900 mb-3">Tracking Information</h4>
                        <div className="space-y-2">
                          {selectedOrder.vehicles?.filter(v => v.tracking_number).map((vehicle, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{vehicle.year} {vehicle.make} {vehicle.model}</p>
                                <p className="text-sm text-blue-700">
                                  {vehicle.tracking_provider || 'Carrier'}: {vehicle.tracking_number}
                                </p>
                              </div>
                              <Badge className="bg-blue-100 text-blue-700">In Transit</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      <PublicFooter />
    </div>
  );
};

export default MyOrders;